var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { useMemo, useState, useRef, useEffect } from "react";
const COOKIE_STORAGE = "cookie";
const LOCAL_STORAGE = "persist";
const SESSION_STORAGE = "session";
const STATE_MANAGEMENT = "state";
const STATE_MANAGEMENT_API_HOST = "https://www.homedepot.com";
const EXPIRE_DAY = "day";
const EXPIRE_MONTH = "month";
const EXPIRE_YEAR = "year";
const encodeStorage = (value) => encodeURIComponent(value).replace(/\(/g, "%28").replace(/\)/g, "%29");
const decodeStorage = (value) => {
  const decodedValue = decodeURIComponent(value);
  return decodedValue;
};
const getOneDayFromNow = () => new Date(Date.now() + 24 * 60 * 60 * 1e3);
const getOneMonthFromNow = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1e3);
const getOneYearFromNow = () => new Date(Date.now() + 365 * 24 * 60 * 60 * 1e3);
const getCookieString = ({
  key,
  value,
  expires,
  path,
  domain
}) => `${key}=${encodeStorage(value)}; expires=${expires}; path=${path}; domain=${domain}`;
const getExpirationDate = (expiration) => {
  let expirationDate = /* @__PURE__ */ new Date();
  if (typeof expiration === "string") {
    switch (expiration) {
      case "day":
        expirationDate = getOneDayFromNow();
        break;
      case "month":
        expirationDate = getOneMonthFromNow();
        break;
      case "year":
      default:
        expirationDate = getOneYearFromNow();
        break;
    }
  } else if (typeof expiration === "number") {
    expirationDate = new Date(expirationDate.getTime() + expiration);
  } else {
    throw new Error("'ttl' must be a number or oneOf ['day', 'month', 'year']");
  }
  return expirationDate.toUTCString();
};
const setWebStorage = ({
  accessType,
  key,
  value,
  ttl = void 0,
  expiration: expirationProp = "year"
}) => {
  const expiration = ttl || expirationProp;
  if (typeof window !== "undefined") {
    const expirationDate = getExpirationDate(expiration);
    const item = JSON.stringify({
      value,
      expiry: expirationDate
    });
    const encodedItem = encodeStorage(item);
    if (accessType === LOCAL_STORAGE) {
      window.localStorage.setItem(key, encodedItem);
    } else if (accessType === SESSION_STORAGE) {
      window.sessionStorage.setItem(key, encodedItem);
    }
  }
};
const removeWebStorage = ({ accessType, key }) => {
  if (typeof window !== "undefined") {
    if (accessType === LOCAL_STORAGE) {
      window.localStorage.removeItem(key);
    } else if (accessType === SESSION_STORAGE) {
      window.sessionStorage.removeItem(key);
    }
  }
};
const getWebStorage = ({ accessType, key, returnString = true }) => {
  let storageValue;
  if (typeof window !== "undefined") {
    if (accessType === LOCAL_STORAGE) {
      storageValue = window.localStorage.getItem(key);
    } else if (accessType === SESSION_STORAGE) {
      storageValue = window.sessionStorage.getItem(key);
    }
  }
  let parsedValue;
  if (storageValue !== null) {
    try {
      const decodedValue = decodeStorage(storageValue);
      parsedValue = JSON.parse(decodedValue);
    } catch (e) {
      parsedValue = null;
    }
  } else {
    parsedValue = null;
  }
  if (new Date(parsedValue == null ? void 0 : parsedValue.expiry) <= Date.now()) {
    parsedValue = null;
    removeWebStorage({ accessType, key });
  }
  const result = parsedValue == null ? void 0 : parsedValue.value;
  if (returnString && result === void 0) {
    return "undefined";
  }
  return result;
};
const webStorage = {
  set: setWebStorage,
  get: getWebStorage,
  remove: removeWebStorage
};
const tryInitStorage = (accessType, key, defaultValue, storageOptions) => {
  if (typeof window === "undefined") {
    return void 0;
  }
  const {
    stringifyInStorage = false,
    ttl,
    expiration
  } = storageOptions;
  let storageValue = webStorage.get({ accessType, key, returnString: false });
  if (stringifyInStorage && storageValue !== null && storageValue !== void 0) {
    try {
      const parsedValue = JSON.parse(storageValue);
      return parsedValue;
    } catch (e) {
      storageValue = void 0;
    }
  }
  if ((storageValue === null || storageValue === void 0) && defaultValue !== void 0) {
    webStorage.set({
      accessType,
      key,
      value: stringifyInStorage ? JSON.stringify(defaultValue) : defaultValue,
      ttl,
      expiration
    });
    storageValue = defaultValue;
  }
  return storageValue;
};
const getHookId = () => {
  var _a, _b, _c;
  if (typeof window !== "undefined" && window.crypto !== void 0 && ((_a = window.crypto) == null ? void 0 : _a.randomUUID) !== void 0) {
    return (_c = (_b = window.crypto) == null ? void 0 : _b.randomUUID()) != null ? _c : Date.now().toString();
  }
  return Date.now().toString();
};
/**
 * @preserve
 * @public
 * @typedef {Object} StorageOptions
 * @property {undefined | number} [ttl=undefined] - Time To Live, the number of milliseconds until expiration
 * @property {'year' | 'month' | 'day'} [expiration="year"] - Domain for cookie storage
 * @property {boolean} [stringifyInStorage=false] - Whether to stringify the value before storing/retrieving it.
 * This is intended for compatibility with THDStorageUtils,
 * where many places in the application will expect stringified values.
 */
/**
 * @preserve
 * @public
 * @type {StorageOptions}
 * */
const defaultStorageOptions = {
  /** @type {undefined | number} */
  ttl: void 0,
  expiration: "year",
  stringifyInStorage: false
};
const useWebStorage = (accessType, key, defaultValue = void 0, storageOptions = defaultStorageOptions) => {
  const mergedOptions = useMemo(() => __spreadValues(__spreadValues({}, defaultStorageOptions), storageOptions), [storageOptions == null ? void 0 : storageOptions.ttl, storageOptions == null ? void 0 : storageOptions.expiration, storageOptions == null ? void 0 : storageOptions.stringifyInStorage]);
  const {
    stringifyInStorage = false,
    ttl,
    expiration
  } = mergedOptions;
  const [storageValue, setStorageValue] = useState(
    () => tryInitStorage(accessType, key, defaultValue, mergedOptions)
  );
  const isMounted = useRef(false);
  const hookId = useRef(getHookId());
  const lifeCycleEventKey = `thdStorageUtils.${accessType === LOCAL_STORAGE ? "localStorage" : "sessionStorage"}`;
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    const newValue = tryInitStorage(accessType, key, defaultValue, mergedOptions);
    setStorageValue(newValue);
  }, [accessType, key, defaultValue, mergedOptions]);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.LIFE_CYCLE_EVENT_BUS === "undefined") {
      return () => {
      };
    }
    const eventHandler = ({ output }) => {
      const { key: eventStorageKey, hookId: eventHookId } = output != null ? output : {};
      if (eventHookId !== hookId.current && eventStorageKey === key) {
        const val = webStorage.get({ accessType, key, returnString: false });
        if (isMounted.current) {
          setStorageValue(stringifyInStorage ? JSON.parse(val) : val);
        }
      }
    };
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(lifeCycleEventKey, eventHandler);
    return () => {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off(lifeCycleEventKey, eventHandler);
    };
  }, [accessType, key, setStorageValue]);
  const setStorageAndTriggerEvents = (value) => {
    if (value === null || value === void 0) {
      webStorage.remove({ accessType, key });
      setStorageValue(void 0);
    } else {
      webStorage.set({
        accessType,
        key,
        value: stringifyInStorage ? JSON.stringify(value) : value,
        ttl,
        expiration
      });
      const newValue = webStorage.get({ accessType, key, returnString: false });
      setStorageValue(stringifyInStorage ? JSON.parse(newValue) : newValue);
    }
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(lifeCycleEventKey, {
      hookId: hookId.current,
      key
    });
  };
  return [storageValue, setStorageAndTriggerEvents];
};
const openDB = (dbName, version, upgradeCB) => {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && typeof window.indexedDB !== "undefined") {
      const dbOpenRequest = window.indexedDB.open(dbName, version);
      dbOpenRequest.onupgradeneeded = (event) => {
        if (upgradeCB) {
          try {
            upgradeCB(event, resolve);
          } catch (e) {
            reject(new Error("Error upgrading db"));
          }
        } else {
          resolve();
        }
      };
      dbOpenRequest.onerror = (event) => {
        reject(event.target.error);
      };
      dbOpenRequest.onsuccess = (event) => {
        resolve(event.target.result);
      };
    } else {
      reject(new Error("indexedDB not available"));
    }
  });
};
const initDB = (dbName, config) => {
  return openDB(dbName, config.version, (event, resolve) => {
    const { result: db, transaction } = event.target;
    const configStores = Object.entries(config.stores);
    const configStoreNames = Object.keys(config.stores);
    configStores.forEach(([objectStoreName, { storeConfig, indexes = [] }]) => {
      if (!db.objectStoreNames.contains(objectStoreName)) {
        db.createObjectStore(objectStoreName, storeConfig);
      }
      const objectStore = transaction.objectStore(objectStoreName);
      const preExistingOSIndexes = [...objectStore.indexNames];
      const configIndexNames = indexes.map(({ name }) => name);
      const storeIndexesToDelete = preExistingOSIndexes.filter((indexName) => !configIndexNames.includes(indexName));
      storeIndexesToDelete.forEach((storeIndex) => {
        objectStore.deleteIndex(storeIndex);
      });
      indexes.forEach(({ name: indexName, unique, multiEntry, keyPath = indexName }) => {
        if (!preExistingOSIndexes.includes(indexName)) {
          objectStore.createIndex(indexName, keyPath, { unique, multiEntry });
        }
        if (preExistingOSIndexes.includes(indexName)) {
          const preExistingIndex = objectStore.index(indexName);
          if (preExistingIndex.unique !== unique || preExistingIndex.multiEntry !== multiEntry || preExistingIndex.keyPath !== keyPath) {
            objectStore.deleteIndex(indexName);
            objectStore.createIndex(indexName, keyPath, { unique, multiEntry });
          }
        }
      });
    });
    [...db.objectStoreNames].forEach((objectStoreName) => {
      if (!configStoreNames.includes(objectStoreName)) {
        db.deleteObjectStore(objectStoreName);
      }
    });
    db.close();
    resolve();
  });
};
const TRANSACTION_TYPES = {
  READ: "readonly",
  WRITE: "readwrite"
};
const openTransaction = (db, storeName, transactionType, onsuccess, onerror) => {
  const transaction = db.transaction(storeName, transactionType);
  transaction.onerror = onerror;
  transaction.onsuccess = onsuccess;
  return transaction;
};
const getObjectStore = (db, storeName, transactionType, onsuccess, onerror) => {
  const transaction = openTransaction(db, storeName, transactionType, onsuccess, onerror);
  return transaction.objectStore(storeName);
};
const getDBFunctions = (dbInfo, storeName) => {
  const getByKey = (key) => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.READ, resolve, reject);
        const request = store.get(key);
        request.onsuccess = (event) => {
          db.close();
          resolve(event.target.result);
        };
        request.onerror = (event) => {
          db.close();
          reject(event.target.error);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const getAll = () => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.READ, resolve, reject);
        const request = store.getAll();
        request.onerror = (event) => {
          db.close();
          reject(new Error(`Error fetching all data for: ${storeName}: ${event.target.error || ""}`));
        };
        request.onsuccess = (event) => {
          db.close();
          resolve(event.target.result);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const add = (value, key) => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const request = store.add(value, key);
        request.onerror = (event) => {
          db.close();
          reject(new Error(`Error adding data for: ${storeName}: ${event.target.error || ""}`));
        };
        request.onsuccess = (event) => {
          db.close();
          resolve(event.target.result);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const remove = (key) => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const request = store.delete(key);
        request.onsuccess = () => {
          db.close();
          resolve();
        };
        request.onerror = (event) => {
          db.close();
          reject(event.target.error);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const update = (value, key) => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const objectStore = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const updateRequest = objectStore.put(value, key);
        updateRequest.onsuccess = () => {
          db.close();
          resolve();
        };
        updateRequest.onerror = (event) => {
          db.close();
          reject(event.target.error);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const clear = () => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const request = store.clear();
        request.onsuccess = () => {
          db.close();
          resolve();
        };
        request.onerror = (event) => {
          db.close();
          reject(event.target.error);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const cursor = (cb, keyRange, direction) => {
    return new Promise((resolve, reject) => {
      openDB(dbInfo.dbName, dbInfo.version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        store.openCursor(keyRange, direction).onsuccess = (event) => {
          const iDBCursor = event.target.result;
          if (iDBCursor) {
            const response = cb(event);
            if (response === "DONE") {
              db.close();
              resolve();
            }
          } else {
            db.close();
            resolve();
          }
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const deleteDB = ({ immediate = true } = {}) => {
    return new Promise((resolve, reject) => {
      const dbOpenRequest = window.indexedDB.deleteDatabase(dbInfo.dbName);
      dbOpenRequest.onsuccess = () => {
        resolve();
      };
      dbOpenRequest.onerror = () => {
        reject();
      };
      if (immediate) resolve();
    });
  };
  return {
    getByKey,
    getAll,
    add,
    remove,
    clear,
    update,
    cursor,
    deleteDB
  };
};
const commonDBInfo = {
  dbName: null,
  version: void 0
};
const initIndexDB = (dbName, config) => __async(void 0, null, function* () {
  commonDBInfo.dbName = dbName;
  commonDBInfo.version = config.version;
  return initDB(dbName, config);
});
const useIndexedDB = (objectStoreName, dbName) => {
  if (dbName) commonDBInfo.dbName = dbName;
  return useMemo(
    () => getDBFunctions(commonDBInfo, objectStoreName),
    [objectStoreName]
  );
};
/**
 * @preserve
 * @public
 * @function useLocalStorage
 * @description React hook that allows access to local storage values,
 *  the same way you would use a useState hook.
 * @param {string} key - Local Storage object key
 * @param {string | Object} [defaultValue] - Default value to use if the key does not exist
 * @param {import('./web-storage/useWebStorage').StorageOptions} [storageOptions=defaultStorageOptions]
 * - Options for local storage
 * @returns {[string | object, function(string | object): void]}
 *  - Returns the value as a string or object, and a function to update the value
 */
const useLocalStorage = (key, defaultValue = void 0, storageOptions = defaultStorageOptions) => {
  return useWebStorage(LOCAL_STORAGE, key, defaultValue, storageOptions);
};
/**
 * @preserve
 * @public
 * @function useSessionStorage
 * @description React hook that allows access to session storage values,
 *  the same way you would use a useState hook.
 * @param {string} key - Local Storage object key
 * @param {string | Object} [defaultValue] - Default value to use if the key does not exist
 * @param {import('./web-storage/useWebStorage').StorageOptions} [storageOptions=defaultStorageOptions]
 * - Options for local storage
 * @returns {[any, (any) => void]}
 *  - Returns the value as a string or object, and a function to update the value
 */
const useSessionStorage = (key, defaultValue = void 0, storageOptions = defaultStorageOptions) => {
  return useWebStorage(SESSION_STORAGE, key, defaultValue, storageOptions);
};
export {
  COOKIE_STORAGE as C,
  EXPIRE_DAY as E,
  LOCAL_STORAGE as L,
  STATE_MANAGEMENT_API_HOST as S,
  getCookieString as a,
  SESSION_STORAGE as b,
  STATE_MANAGEMENT as c,
  decodeStorage as d,
  EXPIRE_MONTH as e,
  EXPIRE_YEAR as f,
  getExpirationDate as g,
  useSessionStorage as h,
  initIndexDB as i,
  useIndexedDB as j,
  useLocalStorage as u,
  webStorage as w
};
